"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useScreenWakeLock } from "@/hooks/useScreenWakeLock";
import { playSound } from "@/lib/game/audio";
import { migratePartId } from "@/lib/game/partIdMigration";
import {
  addStars as applyAddStars,
  claimFlyReward,
  completeActivityDifficulty,
  defaultProgress,
  loadProgress,
  purchasePart,
  recordActivitiesMenuVisit,
  recordActivitySession,
  recordBuilderVisit,
  recordRocketLaunch,
  saveBuiltRocket,
  saveProgress,
  setDifficulty as applyDifficulty,
} from "@/lib/game/progress";
import { starsForDifficulty } from "@/lib/game/stars";
import type { DifficultyLevel, GameScreen, PlacedPart, PlayerProgress } from "@/lib/game/types";
import {
  RewardNotificationsProvider,
  useRewardNotifications,
} from "./rewards/RewardNotifications";

interface GameContextValue {
  screen: GameScreen;
  progress: PlayerProgress;
  goTo: (screen: GameScreen) => void;
  setBuiltRocket: (builtRocket: PlacedPart[]) => void;
  claimFlyReward: (rewardId: string, partId: string) => boolean;
  completeActivity: (activityId: string, difficulty: DifficultyLevel) => void;
  buyPart: (partId: string) => boolean;
  addStars: (amount: number) => void;
  setDifficulty: (level: DifficultyLevel) => void;
  resetProgress: () => void;
  trackBuilderVisit: () => void;
  trackActivitiesMenuVisit: () => void;
  trackActivitySession: (activityId: string) => void;
  trackRocketLaunch: () => void;
}

const GameContext = createContext<GameContextValue | null>(null);

type Listener = () => void;

function createProgressStore() {
  let progress: PlayerProgress | null = null;
  const listeners = new Set<Listener>();

  const getClientSnapshot = (): PlayerProgress => {
    if (progress === null) {
      progress = loadProgress();
    }
    return progress;
  };

  return {
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getServerSnapshot: defaultProgress,
    getClientSnapshot,
    set(next: PlayerProgress) {
      progress = next;
      saveProgress(progress);
      listeners.forEach((l) => l());
    },
    update(updater: (current: PlayerProgress) => PlayerProgress) {
      this.set(updater(getClientSnapshot()));
    },
  };
}

const progressStore = createProgressStore();

function useProgress() {
  return useSyncExternalStore(
    progressStore.subscribe,
    progressStore.getClientSnapshot,
    progressStore.getServerSnapshot,
  );
}

function GameProviderInner({ children }: { children: ReactNode }) {
  useScreenWakeLock(true);
  const { showPartCollect, showStarCollect } = useRewardNotifications();

  const [screen, setScreen] = useState<GameScreen>("home");
  const progress = useProgress();

  const goTo = useCallback((next: GameScreen) => setScreen(next), []);

  const setBuiltRocket = useCallback((builtRocket: PlacedPart[]) => {
    progressStore.update((p) => saveBuiltRocket(p, builtRocket));
  }, []);

  const claimFlyRewardAction = useCallback(
    (rewardId: string, partId: string): boolean => {
      let claimed = false;
      progressStore.update((p) => {
        const next = claimFlyReward(p, rewardId, partId);
        if (!next) return p;
        claimed = true;
        return next;
      });
      if (claimed) {
        playSound("unlock");
        showPartCollect(migratePartId(partId));
      }
      return claimed;
    },
    [showPartCollect],
  );

  const completeActivity = useCallback(
    (activityId: string, difficulty: DifficultyLevel) => {
      const starsEarned = starsForDifficulty(difficulty);
      progressStore.update((p) => {
        const next = completeActivityDifficulty(p, activityId, difficulty);
        playSound("unlock");
        return next;
      });
      showStarCollect(starsEarned);
    },
    [showStarCollect],
  );

  const buyPart = useCallback(
    (partId: string): boolean => {
      let purchased = false;
      progressStore.update((p) => {
        const next = purchasePart(p, partId);
        if (!next) return p;
        purchased = true;
        return next;
      });
      if (purchased) {
        playSound("unlock");
        showPartCollect(partId);
      }
      return purchased;
    },
    [showPartCollect],
  );

  const addStars = useCallback(
    (amount: number) => {
      progressStore.update((p) => applyAddStars(p, amount));
      if (amount > 0) showStarCollect(amount);
    },
    [showStarCollect],
  );

  const setDifficulty = useCallback((level: DifficultyLevel) => {
    progressStore.update((p) => applyDifficulty(p, level));
  }, []);

  const resetProgress = useCallback(() => {
    progressStore.set(defaultProgress());
  }, []);

  const trackBuilderVisit = useCallback(() => {
    progressStore.update((p) => recordBuilderVisit(p));
  }, []);

  const trackActivitiesMenuVisit = useCallback(() => {
    progressStore.update((p) => recordActivitiesMenuVisit(p));
  }, []);

  const trackActivitySession = useCallback((activityId: string) => {
    progressStore.update((p) => recordActivitySession(p, activityId));
  }, []);

  const trackRocketLaunch = useCallback(() => {
    progressStore.update((p) => recordRocketLaunch(p));
  }, []);

  const value = useMemo(
    () => ({
      screen,
      progress,
      goTo,
      setBuiltRocket,
      claimFlyReward: claimFlyRewardAction,
      completeActivity,
      buyPart,
      addStars,
      setDifficulty,
      resetProgress,
      trackBuilderVisit,
      trackActivitiesMenuVisit,
      trackActivitySession,
      trackRocketLaunch,
    }),
    [
      screen,
      progress,
      goTo,
      setBuiltRocket,
      claimFlyRewardAction,
      completeActivity,
      buyPart,
      addStars,
      setDifficulty,
      resetProgress,
      trackBuilderVisit,
      trackActivitiesMenuVisit,
      trackActivitySession,
      trackRocketLaunch,
    ],
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function GameProvider({ children }: { children: ReactNode }) {
  return (
    <RewardNotificationsProvider>
      <GameProviderInner>{children}</GameProviderInner>
    </RewardNotificationsProvider>
  );
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used within GameProvider");
  return ctx;
}
